import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import _ from 'lodash';
import axios, { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';

import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';
import Table from '../../atoms/Table';
import TableHead from '../../atoms/TableHead';
import TableHeader from '../../atoms/TableHeader';
import TableBody from '../../atoms/TableBody';
import TableRow from '../../atoms/TableRow';
import TableData from '../../atoms/TableData';
import ChangePlanCard from '../../molecules/ChangePlanCard';
import MicrosoftSSOWidget from '../../molecules/MicrosoftSSOWidget';
import UnsubscribeCard from '../../molecules/UnsubscribeCard';
import StandardPageTemplate from '../../templates/StandardPage';

function fetchSubscriptionData (subscriptionId: string, cb: (subscriptions: any) => void) {
  axios
    .get(`/apis/microsoft/subscriptions/${subscriptionId}/`)
    .then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK') {
          cb(res.data.subscription);
        } else {
          console.error('<SubscriberSubscriptionDetail> Error fetching subscription details for the subscription.');
        }
      },
      (err: AxiosError) => {
        console.error(`<SubscriberSubscriptionDetail> Error fetching subscription data: ${err}.`);
      },
    );
}

const SubscriberSubscriptionDetailPage = (): JSX.Element => {
  const { graphData, planData, fetchPlanData, error } = useMicrosoft();
  const { subscriptionId } = useParams();
  const [subscriptionData, setSubscriptionData] = useState<any>();
  useEffect(() => {
    if (graphData) {
      fetchSubscriptionData(subscriptionId, setSubscriptionData);
      fetchPlanData(subscriptionId);
    }
  }, [graphData]);

  let pageContent = <></>;
  if (!graphData) {
    pageContent = <MicrosoftSSOWidget error={error} type={'subscriber'} />;
  }
  else if (subscriptionData && planData) {
    const planContent = _.find(planData, {
      planId: subscriptionData.planId,
    });
    pageContent = (
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Key</TableHeader>
            <TableHeader>Value</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableData>Subscription ID</TableData>
            <TableData>{subscriptionData.id}</TableData>
          </TableRow>
          <TableRow>
            <TableData>Subscription Name</TableData>
            <TableData>{subscriptionData.name}</TableData>
          </TableRow>
          {
            planContent &&
              <TableRow>
                <TableData>Plan</TableData>
                <TableData>{planContent.displayName}</TableData>
              </TableRow>
          }
          {
            planContent &&
              <TableRow>
                <TableData>Plan Description</TableData>
                <TableData>{planContent.description}</TableData>
              </TableRow>
          }
          <TableRow>
            <TableData>Start date</TableData>
            <TableData>{dayjs(subscriptionData.term.startDate).format('M/DD/YY H:mm A')}</TableData>
          </TableRow>
          <TableRow>
            <TableData>End date</TableData>
            <TableData>{dayjs(subscriptionData.term.endDate).format('M/DD/YY H:mm A')}</TableData>
          </TableRow>
          <TableRow>
            <TableData>Subscription term</TableData>
            <TableData>{subscriptionData.term.termUnit}</TableData>
          </TableRow>
          <TableRow>
            <TableData>Last modified</TableData>
            <TableData>{dayjs(subscriptionData.lastModified).format('M/DD/YY H:mm A')}</TableData>
          </TableRow>
          <TableRow>
            <TableData>Status</TableData>
            <TableData>{subscriptionData.saasSubscriptionStatus}</TableData>
          </TableRow>
              {
                (subscriptionData.saasSubscriptionStatus === 'Subscribed')
                  &&
                    <TableRow>
                      <TableData>Actions</TableData>
                      <TableData>
                        <ChangePlanCard currentPlan={subscriptionData.planId} planData={planData} subscriptionId={subscriptionId} />
                        <UnsubscribeCard subscriptionId={subscriptionId} />
                      </TableData>
                    </TableRow>
              }
        </TableBody>
      </Table>
    );
  } else {
    pageContent = (
      <p>
        Subscription data has not loaded yet.
      </p>
    );
  }

  return (
    <StandardPageTemplate>
      <h1 className="f-serif f-normal f-italic">
        Subscription Details
      </h1>
      <div>{pageContent}</div>
      {
        graphData &&
          <div>
            <Link className="button td-none" to="/microsoft/subscriber/subscriptions/">View All Subscriptions</Link>
          </div>
      }
    </StandardPageTemplate>
  );
};

export default SubscriberSubscriptionDetailPage;
